import type { ListBuilderConfiguration } from '../types';

/**
 * Client Visits list configuration
 * Converted from JSON to TypeScript for type safety
 */
export const CLIENT_VISITS_LIST_CONFIG: ListBuilderConfiguration = {
	lists: [
		{
			id: 'default-client-visits',
			cacheTTL: 600000,
			name: 'Client Visits',
			type: 'default',
			active: true,
			columns: [
				{
					id: 'visit-date',
					headers: [
						{
							id: 'visit-date-header',
							text: 'Visit Date',
						},
					],
					fields: [
						{
							id: 'visit-date-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'visitDate',
									objectTable: 'client_visit',
								},
							},
							styleConfig: {
								size: 'md',
								colorRule: {
									type: 'default',
								},
								formatOptions: {
									dateFormat: 'datetime-medium',
								},
							},
							displayStyle: 'date',
						},
					],
					sortable: true,
					width: 200,
				},
				{
					id: 'visitor',
					headers: [
						{
							id: 'visitor-header',
							text: 'Logged By',
						},
					],
					fields: [
						{
							id: 'visitor-name',
							dataConfig: {
								type: 'template',
								template: '{firstName} {lastName}',
								templateFields: [],
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'muted',
								},
							},
							displayStyle: 'text',
						},
					],
					sortable: false,
				},
				// PD-15918 (decision 22) — where the rep was when the visit was
				// logged, judged against the client address with the delivery GPS
				// threshold. Two mutually exclusive badges keyed off the API's
				// `gpsProximity` status; a visit with no computable distance
				// (no device GPS, or an un-geocoded client) shows nothing.
				{
					id: 'proximity',
					headers: [
						{
							id: 'proximity-header',
							text: 'Location',
						},
					],
					fields: [
						{
							id: 'proximity-verified',
							dataConfig: {
								type: 'custom_text',
								customText: 'Verified on site',
							},
							styleConfig: {
								size: 'xs',
								colorRule: {
									type: 'static',
									staticColor: { type: 'success' },
								},
							},
							displayStyle: 'badge',
							conditionalVisibility: {
								field: 'gpsProximity',
								condition: 'equals',
								value: 'verified',
							},
						},
						// PD-15924 — the visit was logged by completing a planned stop
						// (decision 12: history shows where a visit came from).
						{
							id: 'from-planned-stop',
							dataConfig: {
								type: 'custom_text',
								customText: 'Planned stop',
							},
							styleConfig: {
								size: 'xs',
								colorRule: {
									type: 'static',
									staticColor: { type: 'info' },
								},
							},
							displayStyle: 'badge',
							conditionalVisibility: {
								field: 'salesTripStopId',
								condition: 'is_not_null',
							},
						},
						{
							id: 'proximity-away',
							dataConfig: {
								type: 'template',
								template: 'Logged {gpsDistanceLabel} away',
								templateFields: [],
							},
							styleConfig: {
								size: 'xs',
								colorRule: {
									type: 'static',
									staticColor: { type: 'warning' },
								},
							},
							displayStyle: 'badge',
							conditionalVisibility: {
								field: 'gpsProximity',
								condition: 'equals',
								value: 'away',
							},
						},
					],
					sortable: false,
					width: 180,
				},
				// PD-16801 — whether the visit happened (completed), was attempted
				// (PD-16957), is scheduled (planned) or fell through (canceled).
				// Only completed visits count as visits in reports and the
				// client's last-visit date.
				{
					id: 'status',
					headers: [
						{
							id: 'status-header',
							text: 'Status',
						},
					],
					fields: [
						{
							id: 'status-completed',
							dataConfig: {
								type: 'custom_text',
								customText: 'Completed',
							},
							styleConfig: {
								size: 'xs',
								colorRule: {
									type: 'static',
									staticColor: { type: 'success' },
								},
							},
							displayStyle: 'badge',
							conditionalVisibility: {
								field: 'status',
								condition: 'equals',
								value: 'completed',
							},
						},
						// PD-16957 added attempted (went, but saw no one); without its own
						// badge the Status cell was blank for those visits (PD-17034).
						{
							id: 'status-attempted',
							dataConfig: {
								type: 'custom_text',
								customText: 'Attempted',
							},
							styleConfig: {
								size: 'xs',
								colorRule: {
									type: 'static',
									staticColor: { type: 'warning' },
								},
							},
							displayStyle: 'badge',
							conditionalVisibility: {
								field: 'status',
								condition: 'equals',
								value: 'attempted',
							},
						},
						{
							id: 'status-planned',
							dataConfig: {
								type: 'custom_text',
								customText: 'Planned',
							},
							styleConfig: {
								size: 'xs',
								colorRule: {
									type: 'static',
									staticColor: { type: 'info' },
								},
							},
							displayStyle: 'badge',
							conditionalVisibility: {
								field: 'status',
								condition: 'equals',
								value: 'planned',
							},
						},
						{
							id: 'status-canceled',
							dataConfig: {
								type: 'custom_text',
								customText: 'Canceled',
							},
							styleConfig: {
								size: 'xs',
								colorRule: {
									type: 'static',
									staticColor: { type: 'neutral' },
								},
							},
							displayStyle: 'badge',
							conditionalVisibility: {
								field: 'status',
								condition: 'equals',
								value: 'canceled',
							},
						},
					],
					sortable: false,
					width: 120,
				},
				// PD-16801 — what the rep recorded about the visit.
				{
					id: 'visit-type',
					headers: [
						{
							id: 'visit-type-header',
							text: 'Type',
						},
					],
					fields: [
						{
							id: 'visit-type-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'visitType',
									objectTable: 'client_visit',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'text',
						},
					],
					sortable: false,
					width: 130,
				},
				{
					id: 'subject',
					headers: [
						{
							id: 'subject-header',
							text: 'Subject',
						},
					],
					fields: [
						{
							id: 'subject-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'subject',
									objectTable: 'client_visit',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'text',
						},
					],
					sortable: false,
					width: 200,
				},
				{
					id: 'contact',
					headers: [
						{
							id: 'contact-header',
							text: 'Contact',
						},
					],
					fields: [
						{
							id: 'contact-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'clientContactName',
									objectTable: 'client_visit',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'text',
						},
					],
					sortable: false,
					width: 160,
				},
				{
					id: 'objective',
					headers: [
						{
							id: 'objective-header',
							text: 'Objective',
						},
					],
					fields: [
						{
							id: 'objective-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'objective',
									objectTable: 'client_visit',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'text',
						},
					],
					sortable: false,
					width: 240,
				},
				{
					id: 'key-takeaways',
					headers: [
						{
							id: 'key-takeaways-header',
							text: 'Key Takeaways',
						},
					],
					fields: [
						{
							id: 'key-takeaways-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'keyTakeaways',
									objectTable: 'client_visit',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'text',
						},
					],
					sortable: false,
					width: 260,
				},
				{
					id: 'next-steps',
					headers: [
						{
							id: 'next-steps-header',
							text: 'Next Steps',
						},
					],
					fields: [
						{
							id: 'next-steps-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'nextSteps',
									objectTable: 'client_visit',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'text',
						},
					],
					sortable: false,
					width: 240,
				},
				{
					id: 'outcome',
					headers: [
						{
							id: 'outcome-header',
							text: 'Outcome',
						},
					],
					fields: [
						{
							id: 'outcome-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'outcome',
									objectTable: 'client_visit',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'text',
						},
					],
					sortable: false,
					width: 160,
				},
				// PD-14327 — what the rep wrote when logging the visit. The note is
				// still a client note (it stays in the client's notes timeline); the
				// API carries its text on the visit row so it reads in context here.
				// Not sortable: `notes` is resolved by subquery, not a visit column.
				{
					id: 'notes',
					headers: [
						{
							id: 'notes-header',
							text: 'Notes',
						},
					],
					fields: [
						{
							id: 'notes-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'notes',
									objectTable: 'client_visit',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'text',
						},
					],
					sortable: false,
					width: 280,
				},
				{
					id: 'created-date',
					headers: [
						{
							id: 'created-date-header',
							text: 'Created',
						},
					],
					fields: [
						{
							id: 'created-date-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: '_created_date',
									objectTable: 'client_visit',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'muted',
								},
								formatOptions: {
									dateFormat: 'datetime-medium',
								},
							},
							displayStyle: 'date',
						},
					],
					sortable: true,
					width: 200,
				},
				{
					id: 'actions',
					headers: [
						{
							id: 'actions-header',
							text: '',
						},
					],
					fields: [
						{
							id: 'actions-dropdown',
							dataConfig: {
								type: 'custom_text',
								customText: '',
							},
							styleConfig: {
								size: 'md',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'actions_dropdown',
							dropdownActions: [
								// PD-17105 — the visit's page, with everything it covered.
								{
									id: 'open-action',
									label: 'Open',
									iconName: 'eye',
									callbackName: 'openClientVisit',
									visibility: 'visible',
									requiredPermission: 'clients:visits:read',
								},
								// PD-16801 — fill in a visit afterwards, or mark a planned
								// visit completed or canceled.
								{
									id: 'edit-action',
									label: 'Edit',
									iconName: 'edit',
									callbackName: 'editClientVisit',
									visibility: 'visible',
									requiredPermission: 'clients:visits:create',
								},
								{
									id: 'delete-action',
									label: 'Delete',
									iconName: 'trash',
									callbackName: 'deleteClientVisit',
									variant: 'danger',
									visibility: 'visible',
									requiredPermission: 'clients:visits:delete',
								},
							],
						},
					],
					sortable: false,
					width: 60,
					align: 'center',
				},
			],
			defaultSortId: 'visit-date-desc',
			sortOrder: 0,
		},
	],
	paging: {
		defaultPageSize: 20,
		pageSizeOptions: [10, 20, 50, 100],
	},
	filters: {
		activeFilters: ['visitDateRange', 'visitUser'],
		defaultFilters: {},
	},
	objects: [
		{
			objectLabel: 'Client Visit',
			objectTable: 'client_visit',
			selectedFields: [
				{
					fieldName: '_id',
					fieldLabel: 'ID',
				},
				{
					fieldName: 'clientId',
					fieldLabel: 'Client ID',
				},
				{
					fieldName: 'visitDate',
					fieldLabel: 'Visit Date',
				},
				{
					fieldName: 'workdUserId',
					fieldLabel: 'User ID',
				},
				{
					fieldName: 'latitude',
					fieldLabel: 'Latitude',
				},
				{
					fieldName: 'longitude',
					fieldLabel: 'Longitude',
				},
				{
					fieldName: 'gpsDistanceMeters',
					fieldLabel: 'Distance From Client (m)',
				},
				{
					fieldName: 'gpsDistanceLabel',
					fieldLabel: 'Distance From Client',
				},
				{
					fieldName: 'gpsValidated',
					fieldLabel: 'GPS Verified',
				},
				{
					fieldName: 'gpsProximity',
					fieldLabel: 'Proximity',
				},
				{
					fieldName: 'notes',
					fieldLabel: 'Notes',
				},
				{
					fieldName: 'status',
					fieldLabel: 'Status',
				},
				{
					fieldName: 'visitType',
					fieldLabel: 'Visit Type',
				},
				{
					fieldName: 'subject',
					fieldLabel: 'Subject',
				},
				{
					fieldName: 'objective',
					fieldLabel: 'Objective',
				},
				{
					fieldName: 'keyTakeaways',
					fieldLabel: 'Key Takeaways',
				},
				{
					fieldName: 'nextSteps',
					fieldLabel: 'Next Steps',
				},
				{
					fieldName: 'clientContactId',
					fieldLabel: 'Contact ID',
				},
				{
					fieldName: 'clientContactName',
					fieldLabel: 'Contact',
				},
				{
					fieldName: 'outcome',
					fieldLabel: 'Outcome',
				},
				{
					fieldName: '_created_date',
					fieldLabel: 'Created Date',
				},
				{
					fieldName: '_creator_userId',
					fieldLabel: 'Creator User ID',
				},
			],
		},
		{
			objectLabel: 'User',
			objectTable: 'user',
			selectedFields: [
				{
					fieldName: 'firstName',
					fieldLabel: 'First Name',
				},
				{
					fieldName: 'lastName',
					fieldLabel: 'Last Name',
				},
				{
					fieldName: 'workdUserId',
					fieldLabel: 'Workd User ID',
				},
			],
		},
	],
	// PD-15683 — defaultSortId must reference a root-level TableSortOption
	// (name + fields); pointing it at a column id resolves no sort fields and
	// logs a ListBuilder warning on every load.
	tableSorts: [
		{
			id: 'visit-date-desc',
			name: 'Visit Date (Newest First)',
			fields: [
				{
					direction: 'DESC',
					fieldName: 'visitDate',
					objectTable: 'client_visit',
				},
			],
		},
	],
};
